import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CONTAINED_CARD_FRAME_HEIGHT, cardHeight, stageReserves, type StageReserves } from '../stageLayout';
import { useIsSleek } from '../../theme';

/** A box on the stage, in css px from the stage's top-left. */
export interface StageRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the winner's own card and plinth would be — for the scene to rest its card there. */
export interface WinnerCardRest {
  card: StageRect;
  pedestal: { left: number; width: number; bottom: number };
}

/**
 * Where the winner screen would put its card and its plinth, measured off
 * an invisible copy of its card slot — the same classes and lengths
 * `WinnerContent` lays the slot out with, so the two can never drift — for
 * the opening scene to rest its card exactly there. The winner then arrives
 * around a card that already sits where its own would have, and nothing is
 * drawn twice.
 *
 * `probe` is the copy, to render inside the stage the scene fills. It
 * measures the moment it attaches — before the scene can mount — and again
 * on resize. `rest` is `null` until then.
 *
 * `contained`: a stage in a box of its own has its host's bands there, as
 * lengths of that box, and the card's frame takes the box's lengths too —
 * the same layout, measured off the box instead of the screen.
 */
export function useWinnerCardRest(contained?: StageReserves): { rest: WinnerCardRest | null; probe: ReactNode } {
  // Same bands in the winner, the reel and the opener scene (see stageLayout).
  const isSleek = useIsSleek();
  const reserves = contained ?? stageReserves(isSleek);
  const nodes = useRef<{
    root: HTMLDivElement | null;
    card: HTMLDivElement | null;
    pedestal: HTMLDivElement | null;
  }>({ root: null, card: null, pedestal: null });
  const [rest, setRest] = useState<WinnerCardRest | null>(null);

  const measure = useCallback(() => {
    const { root, card, pedestal } = nodes.current;
    if (!root || !card || !pedestal) return;
    const origin = root.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    const p = pedestal.getBoundingClientRect();
    if (!c.width || !c.height || !p.width) return;
    const next: WinnerCardRest = {
      card: {
        left: c.left - origin.left,
        top: c.top - origin.top,
        width: c.width,
        height: c.height,
      },
      pedestal: { left: p.left - origin.left, width: p.width, bottom: p.bottom - origin.top },
    };
    setRest((current) => (current && same(current, next) ? current : next));
  }, []);

  const attach = useCallback(
    (key: 'root' | 'card' | 'pedestal') => (node: HTMLDivElement | null) => {
      nodes.current[key] = node;
      if (node) measure();
    },
    [measure],
  );
  const attachRoot = attach('root');
  const attachCard = attach('card');
  const attachPedestal = attach('pedestal');

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // The winner's vertical layout, block for block (see WinnerContent): the
  // title band, the card box in the flexible middle — the card the top of
  // it, the plinth bottom-anchored and narrower — and the panel band
  const probe = (
    <div
      ref={attachRoot}
      aria-hidden
      data-probe="winner-card"
      className="pointer-events-none invisible absolute inset-0 flex flex-col items-center"
    >
      {/* The bands of a contained stage are its host's lengths, with no
          padding of the winner's own under them */}
      <div
        className={contained ? 'w-full shrink-0' : 'w-full shrink-0 pt-16 @[900px]:[@media(min-height:700px)]:pt-25 pb-2'}
        style={{ minHeight: reserves.top }}
      />
      <div className="relative grow min-h-0 w-full flex items-center justify-center">
        <div
          className="relative"
          style={{
            aspectRatio: '320 / 500',
            height: contained ? CONTAINED_CARD_FRAME_HEIGHT : 'min(56vh, 540px, 92vw * 500 / 320)',
            maxHeight: '100%',
          }}
        >
          <div
            ref={attachPedestal}
            className="absolute bottom-0 left-1/2 h-px"
            style={{ width: '82%', transform: 'translateX(-50%)' }}
          />
          <div
            ref={attachCard}
            className="absolute left-1/2 top-0"
            style={{
              height: cardHeight(isSleek),
              aspectRatio: '232 / 367',
              transform: 'translateX(-50%) translateY(-3px)',
            }}
          />
        </div>
      </div>
      <div
        className={contained ? 'w-full shrink-0' : 'w-full shrink-0 px-6 pb-6'}
        style={{ minHeight: reserves.bottom }}
      />
    </div>
  );

  return { rest, probe };
}

function same(a: WinnerCardRest, b: WinnerCardRest): boolean {
  const near = (x: number, y: number) => Math.abs(x - y) < 0.5;
  return (
    near(a.card.left, b.card.left) &&
    near(a.card.top, b.card.top) &&
    near(a.card.width, b.card.width) &&
    near(a.card.height, b.card.height) &&
    near(a.pedestal.left, b.pedestal.left) &&
    near(a.pedestal.width, b.pedestal.width) &&
    near(a.pedestal.bottom, b.pedestal.bottom)
  );
}
